import type { Graph, Thing, WithContext } from 'schema-dts';

/**
 * Render a JSON-LD payload as a server-rendered <script> tag.
 *
 * We render the script inline (rather than via `next/script`) so the structured
 * data is present in the initial SSR HTML. Crawlers and validators that do not
 * execute JavaScript still see the schema, and Googlebot does not have to wait
 * for hydration to discover it.
 *
 * The `<` character is escaped to avoid prematurely closing the surrounding
 * <script> tag if user-generated content ever contains `</script>`. This
 * matches the approach previously provided by `react-schemaorg`'s
 * `jsonLdScriptProps`, letting us drop that dependency.
 */
const renderJsonLd = (item: WithContext<Thing> | Graph | null | undefined) => {
  // `JSON.stringify(undefined)` returns `undefined`, which would render the
  // literal string "undefined". Skip rendering for empty payloads.
  const json = item == null ? undefined : JSON.stringify(item);
  if (!json) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- JSON-LD requires a serialized payload; we sanitize `<` to keep crawlers safe.
      dangerouslySetInnerHTML={{
        __html: json.replaceAll('<', String.raw`\u003c`),
      }}
    />
  );
};

export const JsonLd = <T extends Thing>({ item }: { item: WithContext<T> }) =>
  renderJsonLd(item);

export const JsonLdGraph = ({ item }: { item: Graph }) => renderJsonLd(item);
